import type { TimeZone, WorkingHour } from '@iheartjane/types';

import type { StoreSchedule } from './storeSchedule';
import { buildStoreSchedule } from './storeSchedule';

export type ScheduleType = 'delivery' | 'pickup' | 'curbside' | 'retail';

export type StoreWithSchedules = {
  curbside_hours?: readonly WorkingHour[] | null;
  delivery_hours?: readonly WorkingHour[] | null;
  pickup_hours?: readonly WorkingHour[] | null;
  time_zone_identifier: TimeZone;
  working_hours?: readonly WorkingHour[] | null;
};

interface OrderingScheduleProps {
  store: StoreWithSchedules;
  type: ScheduleType[];
}

export interface OrderingSchedule {
  curbsideSchedule: StoreSchedule | null;
  deliverySchedule: StoreSchedule | null;
  pickupSchedule: StoreSchedule | null;
  retailSchedule: StoreSchedule | null;
}

/**
 * Get the ordering schedule for a store based on the type of schedule
 *
 * Accepts a store object and an array of types to determine which schedules to build.
 */

export const getOrderingSchedule = ({
  store,
  type,
}: OrderingScheduleProps): OrderingSchedule => {
  let deliverySchedule = null;
  let pickupSchedule = null;
  let curbsideSchedule = null;
  let retailSchedule = null;
  const timeZoneIdentifier = store?.time_zone_identifier;

  if (type.includes('delivery') && store?.delivery_hours) {
    deliverySchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'delivery',
      workingHours: [...store.delivery_hours],
    });
  }

  if (type.includes('pickup') && store?.pickup_hours) {
    pickupSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'pickup',
      workingHours: [...store.pickup_hours],
    });
  }

  if (type.includes('curbside') && store?.curbside_hours) {
    curbsideSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'curbside',
      workingHours: [...store.curbside_hours],
    });
  }

  if (type.includes('retail') && store?.working_hours) {
    retailSchedule = buildStoreSchedule({
      timeZoneIdentifier,
      type: 'retail',
      workingHours: [...store.working_hours],
    });
  }

  return {
    curbsideSchedule,
    deliverySchedule,
    pickupSchedule,
    retailSchedule,
  };
};

export const isCurbsideScheduleOpen = (store: StoreWithSchedules): boolean => {
  const { curbsideSchedule } = getOrderingSchedule({
    store,
    type: ['curbside'],
  });
  return curbsideSchedule ? !curbsideSchedule.isAlwaysClosed() : false;
};
